import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { postFetch } from '@legacy/utils/fetcher';
import { POST_PROJECT_SEARCH_KEY } from './postProjectSearchAPI.constants';
import {
  TProjectSearchReq,
  TProjectSearchReqCustomer,
  TProjectSearchReqDepartment,
  TProjectSearchReqLegalEntity,
  TProjectSearchReqProjectManager,
  TProjectSearchReqProjectType,
  TProjectSearchRes,
} from './postProjectSearchAPI.types';
import { ASSIGN_TAKE_ITEMS } from '../../constants';
import { IFilter } from '@/legacy/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItem/FilterItem.types';

const POST_PROJECT_SEARCH_URL_PATH = '/api/v2/resource-planner/project-search';

const postProjectSearch = (
  reqBody: TProjectSearchReq,
): Promise<TProjectSearchRes> =>
  postFetch({
    path: POST_PROJECT_SEARCH_URL_PATH,
    key: POST_PROJECT_SEARCH_KEY,
    body: JSON.stringify(reqBody),
  });

interface IUsePostProjectSearch {
  query: string;
  customer?: IFilter;
  department?: IFilter;
  legalEntity?: IFilter;
  projectManager?: IFilter;
  projectType?: IFilter;
  take?: number;
  skip?: string;
}

export const usePostProjectSearch = ({
  customer: c,
  department: d,
  legalEntity: le,
  projectManager: pm,
  projectType: pt,
  query,
  take = ASSIGN_TAKE_ITEMS,
  skip,
}: IUsePostProjectSearch) => {
  useEffect(
    () => () => {
      // Clean up on unmount
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  const customer: TProjectSearchReqCustomer = c?.values?.length
    ? {
        CustomerIds: c.values.map(({ value }) => value).toString(),
        IsCustomerInclusive: c.isInclude,
      }
    : {
        CustomerIds: undefined,
        IsCustomerInclusive: undefined,
      };

  const department: TProjectSearchReqDepartment = d?.values?.length
    ? {
        DepartmentIds: d.values.map(({ value }) => value).toString(),
        IsDepartmentInclusive: d.isInclude,
      }
    : {
        DepartmentIds: undefined,
        IsDepartmentInclusive: undefined,
      };

  const legalEntity: TProjectSearchReqLegalEntity = le?.values?.length
    ? {
        LegalEntityIds: le.values.map(({ value }) => value).toString(),
        IsLegalEntityInclusive: le.isInclude,
      }
    : {
        LegalEntityIds: undefined,
        IsLegalEntityInclusive: undefined,
      };

  const projectManager: TProjectSearchReqProjectManager = pm?.values?.length
    ? {
        ProjectManagerIds: pm.values.map(({ value }) => value).toString(),
        IsProjectManagerInclusive: pm.isInclude,
      }
    : {
        ProjectManagerIds: undefined,
        IsProjectManagerInclusive: undefined,
      };

  const projectType: TProjectSearchReqProjectType = pt?.values?.length
    ? {
        ProjectTypeIds: pt.values.map(({ value }) => value).toString(),
        IsProjectTypeInclusive: pt.isInclude,
      }
    : {
        ProjectTypeIds: undefined,
        IsProjectTypeInclusive: undefined,
      };

  const reqBody: TProjectSearchReq = {
    SearchText: query.trim(),
    ...customer,
    ...department,
    ...legalEntity,
    ...projectManager,
    ...projectType,
    Take: take,
    Skip: skip,
  };

  const filteredReqBody = Object.keys(reqBody).reduce((a, b) => {
    const key = b as keyof TProjectSearchReq;
    if (
      reqBody[key] !== undefined &&
      reqBody[key] !== null &&
      reqBody[key] !== ''
    ) {
      return { ...a, [key]: reqBody[key] };
    }
    return a;
  }, {} as TProjectSearchReq);

  const {
    data: tempDate,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    ...queryResult
  } = useInfiniteQuery<TProjectSearchRes>({
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 || (lastPage.items?.length ?? 0) < take
        ? undefined // Changed from null to undefined to avoid type issues
        : lastPage.nextSkip,
    queryKey: [POST_PROJECT_SEARCH_KEY, filteredReqBody],
    queryFn: async ({ pageParam }) => {
      const pageParamNumber = Number(pageParam);
      let response: TProjectSearchRes;

      try {
        response = await postProjectSearch({
          ...filteredReqBody,
          Skip: String((filteredReqBody?.Take ?? 0) * pageParamNumber),
        });
      } catch (err) {
        console.error('postProjectSearch failed', err);
        throw err;
      }

      return {
        ...response, // Include all original response properties
        items: response.entities ?? [],
        nextSkip:
          (response.entities?.length ?? 0) < (filteredReqBody?.Take ?? 0)
            ? undefined // Use 0 instead of null since nextSkip expects a number
            : pageParamNumber + 1,
      } as TProjectSearchRes; // Add type assertion to ensure compatibility
    },
  });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (isFetchingNextPage) return;

      // Disconnect previous observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer if we have a node and more pages to fetch
      if (node && hasNextPage) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              fetchNextPage();
            }
          },
          { threshold: 1.0 },
        );

        observerRef.current.observe(node);
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const data = useMemo(
    () => tempDate?.pages?.flatMap((page) => page.items) ?? [],
    [tempDate?.pages],
  );

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    lastElementRef, // Return this instead of observerRef
    latestPageChildrenLength: tempDate?.pages?.at(-1)?.items?.length ?? 0,
    ...queryResult,
  };
};
